import { z } from 'zod';
/**
 * Grupos de estilo com símbolos 3D animados (sem rosto): cada símbolo
 * representa um dos traços de personalidade. Chave: sym-<grupo>-<símbolo>.
 */
export const AVATAR_SYMBOL_GROUPS = ['vidro', 'editorial', 'massinha', 'holografico'];
export const AVATAR_SYMBOLS = ['sol', 'flecha', 'balao', 'diamante', 'lua', 'raio'];
export const AVATAR_SYMBOL_KEYS = AVATAR_SYMBOL_GROUPS.flatMap((group) => AVATAR_SYMBOLS.map((symbol) => `sym-${group}-${symbol}`));
/** Elenco oficial: chave do personagem = família + gênero (ou só a família, quando é neutro). */
const characterCastSchema = z.enum([
    'profissional-ela',
    'profissional-ele',
    'pessoa-ela',
    'pessoa-ele',
    'outra-ela',
    'outra-ele',
    'jovem-ela',
    'jovem-ele',
    'idoso-ela',
    'idoso-ele',
    'crianca-ela',
    'crianca-ele',
    'robo',
    'et',
    'cao',
    'gato',
    'heroi-ela',
    'heroi-ele',
    // Personagens com corpo 3D na conversa ao vivo.
    'robo-s',
    'gota',
    // Pessoa a traço: ilustração a tinta, em vetor, que acompanha a pessoa.
    'traco-ela',
    'traco-ele',
    // A traço por quadros (mapa de bocas e de olhos desenhados): Igor e a nova personagem.
    'traco-igor',
    'traco-ela2',
    // Faces: rostos de massinha/brilho sobre cor chapada.
    'face-sol',
    'face-brasa',
    'face-pessego',
    'face-piscina',
    'face-lavanda',
]);
export const OWN_AVATAR_KEY = /^own-[0-9a-f]{32}$/;
/**
 * Revisão do elenco que o app conhece. Pacotes da loja até o código 4 só conhecem a 1 (sem
 * os personagens a traço) e validam o perfil com a lista fechada: um personagem novo faria
 * a leitura do perfil falhar. O app novo pede `/v1/profile?elenco=2`; sem isso, o servidor
 * entrega um personagem que o app antigo conhece, sem mudar o que está gravado.
 */
export const CAST_REVISION = 4;
/** Revisão em que cada personagem entrou e quem aparece no lugar dele num app mais antigo. */
export const CAST_SINCE = {
    'traco-ela': { revision: 2, fallback: 'gota' },
    'traco-ele': { revision: 2, fallback: 'gota' },
    // Pacotes da revisão 2 conhecem o Caio: o Igor aparece como ele, sem mudar o que está gravado.
    'traco-igor': { revision: 3, fallback: 'traco-ele' },
    // Pacotes até a revisão 3 conhecem a Clara: a nova personagem aparece como ela.
    'traco-ela2': { revision: 4, fallback: 'traco-ela' },
};
/** O personagem como um app de revisão `cast` sabe ler (a troca vale em cadeia). */
export function characterForCast(characterKey, cast) {
    let key = characterKey;
    for (let guard = 0; guard < 4; guard += 1) {
        const since = CAST_SINCE[key];
        if (!since || cast >= since.revision)
            return key;
        key = since.fallback;
    }
    return key;
}
export const characterKeySchema = z.union([
    characterCastSchema,
    z.custom((value) => typeof value === 'string' && AVATAR_SYMBOL_KEYS.includes(value), { message: 'Personagem desconhecido.' }),
    z.custom((value) => typeof value === 'string' && OWN_AVATAR_KEY.test(value), {
        message: 'Personagem desconhecido.',
    }),
]);
export const characterTraitSchema = z.enum([
    'simpatico',
    'direto',
    'divertido',
    'elegante',
    'tranquilo',
    'espontaneo',
]);
export const CHARACTER_TRAITS = characterTraitSchema.options;
export const secretaryStatusSchema = z.enum(['active', 'paused']);
export const secretaryModeSchema = z.enum(['auto', 'review']);
export const ownerStatusSchema = z.enum(['disponivel', 'ocupado']);
const secretaryNameSchema = z.string().trim().min(1).max(40);
const nicknameSchema = z.string().trim().min(1).max(40);
/** Preferências livres do dono (categorias personalizadas, indicação, persona…). */
export const profilePreferencesSchema = z.record(z.string(), z.unknown());
export const profileSchema = z.object({
    characterKey: characterKeySchema,
    traits: z.array(characterTraitSchema).min(1).max(3),
    secretaryName: secretaryNameSchema.nullable(),
    nickname: nicknameSchema.nullable(),
    secretaryStatus: secretaryStatusSchema,
    secretaryMode: secretaryModeSchema,
    city: z.string().nullable(),
    ownerStatus: ownerStatusSchema,
    ownerStatusDetail: z.string().nullable(),
    preferences: profilePreferencesSchema,
    onboardedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
});
export const updateProfileSchema = z
    .object({
    characterKey: characterKeySchema,
    traits: z.array(characterTraitSchema).min(1).max(3),
    secretaryName: secretaryNameSchema.nullable(),
    nickname: nicknameSchema.nullable(),
    secretaryStatus: secretaryStatusSchema,
    secretaryMode: secretaryModeSchema,
    city: z.string().trim().max(60).nullable(),
    ownerStatus: ownerStatusSchema,
    ownerStatusDetail: z.string().trim().max(120).nullable(),
    /** Mesclado por chave com o que já existe; use `null` numa chave para apagá-la. */
    preferences: profilePreferencesSchema,
    onboarded: z.literal(true),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Nada para atualizar.' });
